import { MutableRefObject, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import OtpService from '../../services/otp.service';
import { useNotification } from '../../utils/snackbar';
import { PhoneInputData } from '../PhoneInput/PhoneInput';

enum OtpErrorCodes {
  OTP_INVALID_PHONE_ERROR = 'OTP_INVALID_PHONE_ERROR',
  OTP_REQUEST_FAILED_ERROR = 'OTP_REQUEST_FAILED_ERROR',
  OTP_STATUS_FAILED_ERROR = 'OTP_STATUS_FAILED_ERROR',
  OTP_ALREADY_VERIFIED_ERROR = 'OTP_ALREADY_VERIFIED_ERROR',
  OTP_PHONE_NUMBER_BLOCKED_ERROR = 'OTP_PHONE_NUMBER_BLOCKED_ERROR',
  OTP_TIMEOUT_ERROR = 'OTP_TIMEOUT_ERROR',
}

type UseUserVerificationSendCodeProps = {
  phoneInputRef?: MutableRefObject<HTMLInputElement>;
  onSuccess: () => void;
};

const otpService = new OtpService();

export const useUserVerificationSendCode = (props: UseUserVerificationSendCodeProps) => {
  const { onSuccess, phoneInputRef } = props;

  const { showErrorNotification } = useNotification();
  const { t } = useTranslation();

  const [isLoading, setLoading] = useState(false);
  const [isCtaDisabled, setCtaDisabled] = useState(false);
  const [isPhoneError, setPhoneError] = useState(false);
  const [responseError, setResponseError] = useState('');

  const countdownTimerSecRef = useRef(0);
  const countdownTimerIntervalRef = useRef(null);

  const handleError = (error) => {
    const errorCode: OtpErrorCodes = error?.data?.data?.errorCode;
    let message = error?.data?.data?.message;

    switch (errorCode) {
      // Keep original message for known errors.
      case OtpErrorCodes.OTP_INVALID_PHONE_ERROR:
      case OtpErrorCodes.OTP_REQUEST_FAILED_ERROR:
      case OtpErrorCodes.OTP_STATUS_FAILED_ERROR:
      case OtpErrorCodes.OTP_ALREADY_VERIFIED_ERROR:
      case OtpErrorCodes.OTP_PHONE_NUMBER_BLOCKED_ERROR: {
        break;
      }

      // Timeout message to count-down seconds.
      case OtpErrorCodes.OTP_TIMEOUT_ERROR: {
        const expiresIn = error?.data?.data?.expiresIn ? Math.floor(+error?.data?.data?.expiresIn / 1000) : null;

        if (expiresIn) {
          setCtaDisabled(false);
          triggerExpireCountdown(expiresIn);

          return;
        }

        break;
      }

      // Unknown error.
      default: {
        message = t('common.general-error');

        showErrorNotification({
          message,
          error,
        });
      }
    }

    setResponseError(message);
  };

  const onSubmit = async (phone: PhoneInputData) => {
    setResponseError('');
    setPhoneError(!phone?.parsed);

    if (!phone?.parsed) {
      return;
    }

    phoneInputRef?.current?.blur();

    setLoading(true);

    try {
      await otpService.sendCode({ phoneNumber: phone?.raw }, { silentFail: true });

      onSuccess();
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  const stopExpireCountdown = () => {
    clearInterval(countdownTimerIntervalRef.current);
    setResponseError('');
    setCtaDisabled(false);
  };

  const triggerExpireCountdown = (countdownSec: number) => {
    clearInterval(countdownTimerIntervalRef.current);
    countdownTimerSecRef.current = countdownSec;
    setResponseError(t('components.phone-verification.timeout', { timeoutSec: countdownTimerSecRef.current }));
    setCtaDisabled(true);

    countdownTimerIntervalRef.current = setInterval(() => {
      if (countdownTimerSecRef?.current <= 0) {
        stopExpireCountdown();

        return;
      }

      countdownTimerSecRef.current = countdownTimerSecRef.current - 1;
      setResponseError(t('components.phone-verification.timeout', { timeoutSec: countdownTimerSecRef.current }));
    }, 1000);
  };

  return {
    onSubmit,
    isLoading,
    isCtaDisabled,
    isPhoneError,
    setPhoneError,
    responseError,
    setResponseError,
    stopExpireCountdown,
  };
};
